import { DRIVER_POLICIES, type DriverPolicy } from '@zuruflex/types';
import { FormField, Input, cn } from '@zuruflex/ui';
import { useTranslation } from 'react-i18next';

/**
 * The controls a car's details need, in the two places that edit them: the
 * dialog that adds a car and the form on its own page that corrects one.
 *
 * They live here rather than in either so the two cannot drift. A plate
 * typed into the add dialog and the same plate corrected a week later are
 * the same field, and a partner who learned it once should not meet a
 * different control the second time.
 */

/** A row of buttons instead of a select: fewer taps on a phone. */
export function Choice<T extends string>({
  label,
  options,
  value,
  onChange,
  render,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  render: (option: T) => string;
}) {
  return (
    <fieldset>
      <legend className="mb-1.5 text-sm font-medium text-text">{label}</legend>
      <div className="flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={value === option}
            onClick={() => onChange(option)}
            className={cn(
              'rounded-field border px-2.5 py-1.5 text-xs font-medium transition-colors',
              value === option
                ? 'border-surface-inverse bg-surface-inverse text-text-inverse'
                : 'border-border-default hover:border-border-strong',
            )}
          >
            {render(option)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Whether this car is let with a driver, and for how much.
 *
 * Hidden entirely for a partner who supplies none — the choice would be a
 * dead end, and the API refuses it anyway — with a line saying where to turn
 * it on, so the absence is explained rather than merely unexplained.
 */
export function DriverFields({
  available,
  currency,
  policy,
  onPolicyChange,
  rate,
  onRateChange,
}: {
  available: boolean;
  currency: string;
  policy: DriverPolicy;
  onPolicyChange: (value: DriverPolicy) => void;
  rate: string;
  onRateChange: (value: string) => void;
}) {
  const { t } = useTranslation(['partner', 'common']);

  if (!available) {
    return <p className="text-xs text-text-muted">{t('addCar.driverNeedsSettings')}</p>;
  }

  return (
    <>
      <Choice
        label={t('addCar.driverPolicy')}
        options={DRIVER_POLICIES}
        value={policy}
        onChange={onPolicyChange}
        render={(option) => t(`common:driverPolicy.${option}`)}
      />

      {policy === 'SELF_DRIVE_ONLY' ? null : (
        <FormField
          label={t('addCar.driverRate', { currency })}
          hint={t('addCar.driverRateHint')}
          required
        >
          {(field) => (
            <Input
              {...field}
              type="number"
              min={1}
              step="1"
              value={rate}
              required
              placeholder="120"
              onChange={(event) => onRateChange(event.target.value)}
            />
          )}
        </FormField>
      )}
    </>
  );
}
